import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from '@roger/ui-components';
import { Building2, Loader2, Plus, Search } from 'lucide-react';
import {
  bulkAssignClinicRolesToUser,
  createAgency,
  getAgencyMembers,
  inviteUser,
  listAgenciesWithAssignedRoles,
  listRoles,
  listUserRoleAssignments,
  removeUserFromAgency,
  updateClinicAccess,
  updateUserRoles,
} from '../../services/api';
import type {
  AgencyResponse,
  AgencyWithAssignedRolesResponse,
  UserManagerUserResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import type { AgencyState, UserRolesState } from './types';
import AgencyRow from './components/AgencyRow';
import EditUserRolesDialog from './components/EditUserRolesDialog';
import { getAgencyClinics } from '../../services/clinicsApi';
import { withAgencyClinicDisplayNames } from '../../utils/agencyClinicDisplayName';
import type { EditUserRolesSave } from './components/EditUserRolesDialog';
import InviteUserToAgencyDialog, {
  type InviteUserProfileDraft,
} from '../../pages/RBAC/components/InviteUserToAgencyDialog';
import ConfirmRemoveDialog from './components/ConfirmRemoveDialog';
import MergeAccountDialog from './components/MergeAccountDialog';
import CreateAgencyDialog from './components/CreateAgencyDialog';
import { filterUserVisibleRoles } from '../../utils/rbacRoles';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
// Every save on the edit dialog reconciles against what these two reads
// returned, so a read that filled its page would silently revoke whatever
// fell off the end.
//
// The clinic list is asked for one over the limit, so a full page is proof
// there are more rather than a guess. The assignments endpoint caps limit at
// 5000 server-side and takes no offset, so there is no way to over-fetch or
// probe past it: a full page is treated as too many, which refuses a save at
// exactly 5000 that would have been safe.
const ROLE_ASSIGNMENT_READ_LIMIT = 5000;
const CLINIC_READ_LIMIT = 1000;

const SuperUserTab: React.FC = () => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [agencies, setAgencies] = useState<AgencyWithAssignedRolesResponse[]>(
    []
  );
  const [totalAgencies, setTotalAgencies] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoadingAgencies, setIsLoadingAgencies] = useState(true);

  const [agencyStates, setAgencyStates] = useState<Record<string, AgencyState>>(
    {}
  );

  const [editingUser, setEditingUser] = useState<{
    agency: AgencyResponse;
    user: UserManagerUserResponse;
  } | null>(null);
  const [userRolesState, setUserRolesState] = useState<UserRolesState>({
    roles: null,
    availableRoles: null,
    isLoading: false,
    clinics: [],
    availableClinicRoles: [],
    clinicRoleClinicIds: {},
    clinicAccessIds: [],
    loaded: false,
    readTruncated: false,
  });

  const [addUserTarget, setAddUserTarget] = useState<AgencyResponse | null>(
    null
  );

  const [removeTarget, setRemoveTarget] = useState<{
    agency: AgencyResponse;
    user: UserManagerUserResponse;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const [mergeTarget, setMergeTarget] = useState<AgencyResponse | null>(null);

  const [showCreateAgency, setShowCreateAgency] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Track the latest in-flight load so out-of-order responses can't overwrite
  // newer state with older data (search + pagination changes can race).
  const loadTokenRef = useRef(0);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  // ---------------------------------------------------------------------------
  // Load a page of agencies (with assigned roles inline, server-side search).
  // Replaces the previous "load all + N×listDistinctRolesAssignedAtAgency"
  // pattern that caused a request storm on each page load. We also call this
  // after every mutation, so role badges stay fresh without a separate
  // per-agency refresh endpoint.
  // ---------------------------------------------------------------------------

  const loadAgencies = useCallback(async () => {
    const token = ++loadTokenRef.current;
    setIsLoadingAgencies(true);
    try {
      const res = await listAgenciesWithAssignedRoles({
        query: {
          search: debouncedSearch || undefined,
          skip: page * PAGE_SIZE,
          limit: PAGE_SIZE,
        },
      });

      if (token !== loadTokenRef.current) return;

      const items = res.data?.items ?? [];
      const total = res.data?.total ?? 0;
      setAgencies(items);
      setTotalAgencies(total);
      setAgencyStates(prev =>
        Object.fromEntries(
          items.map(a => {
            const previous = prev[a.id];
            return [
              a.id,
              {
                users: previous?.users ?? null,
                isLoadingUsers: previous?.isLoadingUsers ?? false,
                isExpanded: previous?.isExpanded ?? false,
                assignedRoles: filterUserVisibleRoles(a.assigned_roles ?? []),
              } satisfies AgencyState,
            ];
          })
        )
      );
    } catch (error) {
      if (token !== loadTokenRef.current) return;
      console.error('Failed to load agencies:', error);
      showError(
        'Failed to Load Agencies',
        getUserFacingApiMessage(
          error,
          'Unable to load agencies. Please try again.'
        )
      );
    } finally {
      if (token === loadTokenRef.current) {
        setIsLoadingAgencies(false);
      }
    }
  }, [debouncedSearch, page, showError]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  // The client answers errors as values, so a failed read keeps the old list
  // and returns false.
  const refreshAgencyMembers = useCallback(
    async (agencyId: string): Promise<boolean> => {
      const res = await getAgencyMembers({ query: { agency_id: agencyId } });
      if (res.error || res.data === undefined) {
        console.error('Failed to refresh agency members:', res.error);
        return false;
      }
      const users = res.data;
      setAgencyStates(prev => ({
        ...prev,
        [agencyId]: { ...prev[agencyId], users },
      }));
      return true;
    },
    []
  );

  // -------------------------------------------------------------------------
  // Expand / collapse agency (lazy load users)
  // -------------------------------------------------------------------------

  const handleToggleExpand = useCallback(
    async (agencyId: string) => {
      const current = agencyStates[agencyId];
      if (!current) return;

      const willExpand = !current.isExpanded;
      setAgencyStates(prev => ({
        ...prev,
        [agencyId]: { ...prev[agencyId], isExpanded: willExpand },
      }));

      if (willExpand && current.users === null) {
        setAgencyStates(prev => ({
          ...prev,
          [agencyId]: { ...prev[agencyId], isLoadingUsers: true },
        }));
        try {
          if (!(await refreshAgencyMembers(agencyId))) {
            showError(
              'Failed to Load Users',
              'Unable to load users. Please try again.'
            );
          }
        } catch (error) {
          console.error('Failed to load agency users:', error);
          showError(
            'Failed to Load Users',
            getUserFacingApiMessage(
              error,
              'Unable to load users. Please try again.'
            )
          );
        } finally {
          setAgencyStates(prev => ({
            ...prev,
            [agencyId]: { ...prev[agencyId], isLoadingUsers: false },
          }));
        }
      }
    },
    [agencyStates, refreshAgencyMembers, showError]
  );

  // -------------------------------------------------------------------------
  // Edit user roles
  // -------------------------------------------------------------------------

  const handleEditUser = useCallback(
    async (agency: AgencyResponse, user: UserManagerUserResponse) => {
      setEditingUser({ agency, user });
      setUserRolesState({
        roles: null,
        availableRoles: null,
        isLoading: true,
        clinics: [],
        availableClinicRoles: [],
        clinicRoleClinicIds: {},
        clinicAccessIds: [],
        loaded: false,
        readTruncated: false,
      });

      try {
        const [rolesRes, availableRes, clinics, membersRes] = await Promise.all(
          [
            listUserRoleAssignments({
              path: { user_id: user.id },
              // Every save here reconciles against what this read returned, so
              // the default page of 100 would revoke whatever fell off the end.
              query: {
                agency_id: agency.id,
                limit: ROLE_ASSIGNMENT_READ_LIMIT,
              },
            }),
            listRoles({
              query: { agency_id: agency.id },
            }),
            // The agency being administered, not the operator's own.
            getAgencyClinics(agency.id, 0, CLINIC_READ_LIMIT + 1),
            // Clinic access is reconciled on save, so it is read here rather
            // than taken from the roster snapshot, which was loaded when the
            // agency row was expanded. Access granted since then would
            // otherwise seed as absent and be revoked.
            getAgencyMembers({ query: { agency_id: agency.id } }),
          ]
        );
        const assignments = rolesRes.data ?? [];
        const tooManyClinics = clinics.length > CLINIC_READ_LIMIT;
        const tooManyAssignments =
          assignments.length >= ROLE_ASSIGNMENT_READ_LIMIT;
        if (tooManyClinics || tooManyAssignments) {
          showError(
            'Cannot Edit This User',
            tooManyClinics
              ? `This agency has more than ${CLINIC_READ_LIMIT} clinics, which is more than this dialog can read at once. Saving would revoke access to the clinics it could not see.`
              : `This user has ${ROLE_ASSIGNMENT_READ_LIMIT} or more role assignments, which is more than this dialog can read at once. Saving would revoke the assignments it could not see.`
          );
          setUserRolesState(prev => ({
            ...prev,
            isLoading: false,
            loaded: false,
            readTruncated: true,
          }));
          return;
        }
        // Fall back to the roster snapshot only if the fresh read no longer
        // lists this person, which a save would fail on anyway.
        const freshUser = (membersRes.data ?? []).find(m => m.id === user.id);
        const freshClinicAccessIds =
          freshUser?.clinic_access_ids ?? user.clinic_access_ids ?? [];
        const allAvailable = availableRes.data ?? [];
        const agencyScopedAll = allAvailable
          .filter(r => r.scope !== 'clinic')
          .sort(
            (a, b) =>
              Number(a.is_internal ?? false) - Number(b.is_internal ?? false)
          );
        const agencyScopedAvailable = filterUserVisibleRoles(agencyScopedAll);
        const agencyScopedRoleIds = new Set(agencyScopedAll.map(r => r.id));
        const userRoles = assignments.filter(r =>
          agencyScopedRoleIds.has(r.role_id)
        );
        // Clinic-scoped roles were filtered out here, which is why this dialog
        // could never repair somebody's clinic access.
        const clinicScopedAvailable = filterUserVisibleRoles(
          allAvailable.filter(r => r.scope === 'clinic')
        );
        const clinicRoleIds = new Set(clinicScopedAvailable.map(r => r.id));
        const clinicRoleClinicIds: Record<string, string[]> = {};
        for (const assignment of assignments) {
          if (!assignment.clinic_id || !clinicRoleIds.has(assignment.role_id)) {
            continue;
          }
          (clinicRoleClinicIds[assignment.role_id] ??= []).push(
            assignment.clinic_id
          );
        }
        setUserRolesState({
          roles: userRoles,
          availableRoles: agencyScopedAvailable,
          isLoading: false,
          clinics: withAgencyClinicDisplayNames(clinics).sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          availableClinicRoles: clinicScopedAvailable,
          clinicRoleClinicIds,
          clinicAccessIds: freshClinicAccessIds,
          loaded: true,
          readTruncated: false,
        });
      } catch (error) {
        console.error('Failed to load user roles:', error);
        showError(
          'Failed to Load Roles',
          getUserFacingApiMessage(
            error,
            'Unable to load role data. Please try again.'
          )
        );
        setUserRolesState(prev => ({ ...prev, isLoading: false }));
      }
    },
    [showError]
  );

  const handleSaveRoles = useCallback(
    async (save: EditUserRolesSave) => {
      if (!editingUser) return;
      const agencyId = editingUser.agency.id;
      const userId = editingUser.user.id;
      // Three writes, three transactions. A failure partway through has
      // already committed the earlier ones, so the message says which.
      let savedAgencyRoles = false;
      let savedClinicRoles = false;
      try {
        // Without throwOnError the client returns errors instead of throwing,
        // so this path used to toast success even when the API rejected the
        // save (common when "select all" includes restricted roles).
        await updateUserRoles({
          path: { user_id: userId },
          query: { agency_id: agencyId },
          body: { role_ids: save.roleIds },
          throwOnError: true,
        });
        // Clinic roles before access: granting a role creates the membership
        // row, so reconciling access afterwards sees the same set the dialog
        // computed and cannot revoke what a role just granted.
        savedAgencyRoles = true;
        await bulkAssignClinicRolesToUser({
          path: { user_id: userId },
          query: { agency_id: agencyId },
          body: {
            assignments: Object.entries(save.clinicRoleClinicIds).flatMap(
              ([roleId, clinicIds]) =>
                clinicIds.map(clinicId => ({
                  role_id: roleId,
                  clinic_id: clinicId,
                }))
            ),
          },
          throwOnError: true,
        });
        savedClinicRoles = true;
        await updateClinicAccess({
          path: { user_id: userId },
          query: { agency_id: agencyId },
          body: { clinic_ids: save.clinicIds },
          throwOnError: true,
        });
        showSuccess('Roles and clinic access updated successfully.');
        setEditingUser(null);

        // Reload agencies first (updates role badges, preserves isExpanded).
        await loadAgencies();

        // Then refresh the users list for this agency so name/info changes are
        // immediately visible without requiring the user to collapse and re-expand.
        try {
          await refreshAgencyMembers(agencyId);
        } catch (refreshError) {
          console.error('Failed to refresh agency members:', refreshError);
        }
      } catch (error) {
        console.error('Failed to update user roles:', error);
        const applied = savedClinicRoles
          ? 'Agency roles and clinic roles were saved, but clinic access was not.'
          : savedAgencyRoles
            ? 'Agency roles were saved, but clinic roles and clinic access were not.'
            : 'Nothing was saved.';
        showError(
          'Failed to Update Roles',
          `${applied} ${getUserFacingApiMessage(
            error,
            'Please reopen and try again.'
          )}`
        );
      }
    },
    [editingUser, loadAgencies, refreshAgencyMembers, showError, showSuccess]
  );

  // A merge or its reversal changes members and roles, so the member list and
  // the role badges are both stale afterwards.
  const handleAccountsChanged = useCallback(
    async (agencyId: string) => {
      void loadAgencies();
      await refreshAgencyMembers(agencyId);
    },
    [loadAgencies, refreshAgencyMembers]
  );

  // -------------------------------------------------------------------------
  // Remove user from agency
  // -------------------------------------------------------------------------

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    try {
      await removeUserFromAgency({
        path: {
          agency_id: removeTarget.agency.id,
          user_id: removeTarget.user.id,
        },
      });
      showSuccess('User removed from agency.');
      setRemoveTarget(null);

      setAgencyStates(prev => ({
        ...prev,
        [removeTarget.agency.id]: {
          ...prev[removeTarget.agency.id],
          users: (prev[removeTarget.agency.id]?.users ?? []).filter(
            u => u.id !== removeTarget.user.id
          ),
        },
      }));
      void loadAgencies();
    } catch (error) {
      console.error('Failed to remove user from agency:', error);
      showError(
        'Failed to Remove User',
        getUserFacingApiMessage(
          error,
          'Unable to remove user. Please try again.'
        )
      );
    } finally {
      setIsRemoving(false);
    }
  }, [loadAgencies, removeTarget, showError, showSuccess]);

  // -------------------------------------------------------------------------
  // Add user to agency
  // -------------------------------------------------------------------------

  const handleAddUser = useCallback(
    async (params: {
      emails: string[];
      agency_id: string;
      role_ids: string[];
      profiles: InviteUserProfileDraft[];
      clinic_ids: string[] | null;
    }) => {
      if (!addUserTarget) return;

      try {
        // Without throwOnError a 403 (e.g. Super Admin on a non-internal
        // invitee) resolves instead of throwing, res.data is empty, and we
        // fall through to "Invitation sent." — same class of bug as the role save.
        const res = await inviteUser({
          body: {
            emails: params.emails,
            role_ids: params.role_ids,
            agency_id: params.agency_id,
            profiles: params.profiles,
            // What the operator ticked. Dropping it sent `undefined`, which
            // the backend reads as "every clinic the inviter can access" --
            // so a narrowed selection silently granted the whole agency.
            clinic_ids: params.clinic_ids,
          },
          throwOnError: true,
        });
        const data = res.data;
        // Invited, but Roger's email for the invitation did not go out.
        const emailFailures =
          data?.results.filter(r => r.success && r.email_failed).length ?? 0;
        if (emailFailures > 0) {
          showError(
            'Invitation Emails Not Sent',
            `${emailFailures} invited user(s) did not get the invitation email. Use Resend invite from the User Manager.` +
              (data && data.failed_count > 0
                ? ` ${data.failed_count} invitation(s) failed.`
                : '')
          );
        } else if (data && data.failed_count > 0 && data.invited_count > 0) {
          showSuccess(
            `${data.invited_count} of ${params.emails.length} invitations sent. ${data.failed_count} failed.`
          );
        } else if (data && data.failed_count === params.emails.length) {
          showError(
            'Failed to Add Users',
            'Unable to send invitations. Please try again.'
          );
          return;
        } else {
          showSuccess('Invitation sent.');
        }
        setAddUserTarget(null);
        void loadAgencies();

        const agencyState = agencyStates[params.agency_id];
        if (agencyState?.isExpanded) {
          setAgencyStates(prev => ({
            ...prev,
            [params.agency_id]: {
              ...prev[params.agency_id],
              isLoadingUsers: true,
            },
          }));
          try {
            if (!(await refreshAgencyMembers(params.agency_id))) {
              showError(
                'Failed to Refresh Members',
                'Unable to reload member list. Please try again.'
              );
            }
          } catch (refreshError) {
            console.error('Failed to refresh agency members:', refreshError);
            showError(
              'Failed to Refresh Members',
              getUserFacingApiMessage(
                refreshError,
                'Unable to reload member list. Please try again.'
              )
            );
          } finally {
            setAgencyStates(prev => ({
              ...prev,
              [params.agency_id]: {
                ...prev[params.agency_id],
                isLoadingUsers: false,
              },
            }));
          }
        }
      } catch (error) {
        console.error('Failed to add user to agency:', error);
        showError(
          'Failed to Add User',
          getUserFacingApiMessage(
            error,
            'Unable to invite user. Please try again.'
          )
        );
      }
    },
    [
      addUserTarget,
      agencyStates,
      loadAgencies,
      refreshAgencyMembers,
      showError,
      showSuccess,
    ]
  );

  // -------------------------------------------------------------------------
  // Create agency
  // -------------------------------------------------------------------------

  const handleCreateAgency = useCallback(
    async (data: {
      system_name: string;
      written_name: string;
      abbreviation: string;
      is_kinnser_enabled: boolean;
      add_onboarding_team: boolean;
      sync_clinic_info: boolean;
    }) => {
      try {
        const { data: createdAgency, error } = await createAgency({
          body: {
            ...data,
            skip_workos_organization: false,
          },
        });
        if (error) {
          throw error;
        }

        const warnings =
          (createdAgency as { warnings?: string[] } | undefined)?.warnings ??
          [];
        if (warnings.length > 0) {
          showError('Agency Created with Warnings', warnings.join(' '));
        } else {
          showSuccess('Agency created successfully.');
        }
        setShowCreateAgency(false);
        await loadAgencies();
      } catch (error) {
        console.error('Failed to create agency:', error);
        showError(
          'Failed to Create Agency',
          getUserFacingApiMessage(
            error,
            'Unable to create agency. Please try again.'
          )
        );
      }
    },
    [loadAgencies, showError, showSuccess]
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const totalPages = Math.max(1, Math.ceil(totalAgencies / PAGE_SIZE));
  const showingFrom = totalAgencies === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, totalAgencies);

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between gap-4 py-4 px-5'>
          <div>
            <CardTitle className='text-base'>All Agencies</CardTitle>
            <p className='text-sm text-muted-foreground'>
              Manage users and roles across every agency in the system.
            </p>
          </div>
          <Button
            size='sm'
            onClick={() => setShowCreateAgency(true)}
            className='gap-1.5 flex-shrink-0'
          >
            <Plus className='h-4 w-4' />
            New agency
          </Button>
        </CardHeader>

        <CardContent className='p-0 border-t border-border/50'>
          <div className='px-5 py-3 border-b border-border/50 bg-muted/20'>
            <label htmlFor='superuser-agency-search' className='sr-only'>
              Search agencies by name, system name, or abbreviation
            </label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none' />
              <Input
                id='superuser-agency-search'
                type='search'
                placeholder='Search by agency name, system name, or abbreviation…'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                inputSize='default'
                className='pl-10'
                autoComplete='off'
              />
            </div>
          </div>

          {isLoadingAgencies && agencies.length === 0 ? (
            <div className='flex items-center justify-center py-16 gap-2 text-muted-foreground'>
              <Loader2 className='h-5 w-5 animate-spin' />
              <span>Loading agencies…</span>
            </div>
          ) : agencies.length === 0 ? (
            debouncedSearch ? (
              <div className='flex flex-col items-center justify-center py-12 gap-1 text-muted-foreground'>
                <Search className='h-5 w-5' />
                <p className='text-sm'>
                  No agencies match &quot;{debouncedSearch}&quot;
                </p>
              </div>
            ) : (
              <div className='flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground'>
                <Building2 className='h-8 w-8' />
                <p>No agencies found.</p>
              </div>
            )
          ) : (
            <div className='divide-y divide-border/50'>
              {agencies.map(agency => {
                const state = agencyStates[agency.id];
                if (!state) return null;
                return (
                  <AgencyRow
                    key={agency.id}
                    agency={agency}
                    state={state}
                    onToggleExpand={handleToggleExpand}
                    onEditUser={handleEditUser}
                    onRemoveUser={(a, u) =>
                      setRemoveTarget({ agency: a, user: u })
                    }
                    onAddUser={a => setAddUserTarget(a)}
                    onMergeAccounts={a => setMergeTarget(a)}
                  />
                );
              })}
            </div>
          )}

          {totalAgencies > 0 && (
            <div className='flex items-center justify-between gap-3 px-5 py-3 border-t border-border/50 bg-muted/10'>
              <span className='text-xs text-muted-foreground'>
                {isLoadingAgencies ? (
                  <span className='inline-flex items-center gap-1'>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    Loading…
                  </span>
                ) : (
                  <>
                    Showing {showingFrom}–{showingTo} of {totalAgencies}
                  </>
                )}
              </span>
              <div className='flex gap-2'>
                <Button
                  size='sm'
                  variant='outline'
                  disabled={page <= 0 || isLoadingAgencies}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  disabled={page >= totalPages - 1 || isLoadingAgencies}
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <EditUserRolesDialog
        open={editingUser !== null}
        agency={editingUser?.agency ?? null}
        user={editingUser?.user ?? null}
        rolesState={userRolesState}
        onClose={() => setEditingUser(null)}
        onSave={handleSaveRoles}
      />

      <InviteUserToAgencyDialog
        open={addUserTarget !== null}
        agencies={agencies}
        agencyId={addUserTarget?.id ?? null}
        onClose={() => setAddUserTarget(null)}
        onInvite={handleAddUser}
      />

      <ConfirmRemoveDialog
        open={removeTarget !== null}
        agency={removeTarget?.agency ?? null}
        user={removeTarget?.user ?? null}
        isRemoving={isRemoving}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleConfirmRemove}
      />

      <MergeAccountDialog
        key={mergeTarget?.id ?? 'closed'}
        scope={mergeTarget && { agency: mergeTarget }}
        onClose={() => setMergeTarget(null)}
        onAccountsChanged={() => {
          if (mergeTarget) void handleAccountsChanged(mergeTarget.id);
        }}
      />

      <CreateAgencyDialog
        open={showCreateAgency}
        onClose={() => setShowCreateAgency(false)}
        existingAgencies={agencies}
        onCreate={handleCreateAgency}
      />
    </div>
  );
};

export default SuperUserTab;
